import { coreApi } from '.';
import type {
  BasicFilter,
  CreateRoleRequest,
  CreateRoleResponse,
  GetRoleByIDResponse,
  GetRoleByIdRequest,
  GetRolesResponse,
  RemoveUsersByRoleRequest,
  UpdateRoleRequest,
  UpdateUserRequest,
} from 'types';
import { TAG_TYPES } from 'types/services';
import type { roleDetailDataScheme, rolesDataScheme } from 'utils/table/admin/core/roles';

export const rolesService = coreApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getAllRoles: builder.query<GetRolesResponse, BasicFilter<typeof rolesDataScheme>>({
      query: (data) => ({
        url: 'Roles',
        method: 'GET',
        params: {
          Search: data.search,
          OrderBy: data.orderBy,
          OrderDesc: data.orderDesc,
          Page: data.page,
          PageSize: data.pageSize,
        },
      }),
      providesTags: [TAG_TYPES.ROLES],
    }),
    getRoleById: builder.query<GetRoleByIDResponse, GetRoleByIdRequest<typeof roleDetailDataScheme>>({
      query: (data) => ({
        url: `Roles/${data.id}`,
        method: 'GET',
        params: {
          Search: data.search,
          OrderBy: data.orderBy,
          OrderDesc: data.orderDesc,
          Page: data.page,
          PageSize: data.pageSize,
        },
      }),
      providesTags: (result) => (result ? [{ type: TAG_TYPES.ROLE_DETAIL, id: result.id }] : [{ type: TAG_TYPES.ROLE_DETAIL }]),
    }),
    createRole: builder.mutation<CreateRoleResponse, CreateRoleRequest>({
      query: (data) => ({
        url: 'Roles',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [TAG_TYPES.ROLES],
    }),
    removeRole: builder.mutation<void, string>({
      query: (data) => ({
        url: `Roles/${data}`,
        method: 'DELETE',
      }),
      invalidatesTags: [TAG_TYPES.ROLES],
    }),
    removeRoles: builder.mutation<void, string[]>({
      query: (data) => ({
        url: 'Roles',
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: [TAG_TYPES.ROLES],
    }),
    removeUsersByRole: builder.mutation<void, RemoveUsersByRoleRequest>({
      query: ({ id, userIds }) => ({
        url: `Roles/${id}/Users`,
        method: 'DELETE',
        body: userIds,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: [{ type: TAG_TYPES.ROLES }, { type: TAG_TYPES.ROLE_DETAIL }],
    }),
    updateRole: builder.mutation<CreateRoleResponse, UpdateRoleRequest>({
      query: ({ id, ...data }) => ({
        url: `Roles/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result) =>
        result
          ? [{ type: TAG_TYPES.ROLES }, { type: TAG_TYPES.ROLE_DETAIL, id: result.id }]
          : [{ type: TAG_TYPES.ROLES }, { type: TAG_TYPES.ROLE_DETAIL }],
    }),
    addUserByRole: builder.mutation<void, UpdateUserRequest>({
      query: ({ id, data }) => ({
        url: `Roles/${id}/Users`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [{ type: TAG_TYPES.ROLES }, { type: TAG_TYPES.ROLE_DETAIL }],
    }),
    updateUserByRole: builder.mutation<void, UpdateUserRequest>({
      query: ({ id, data }) => ({
        url: `Roles/${id}/Users`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: [{ type: TAG_TYPES.ROLES }, { type: TAG_TYPES.ROLE_DETAIL }],
    }),
    getDistinctTags: builder.query<{ tags: string[] }, void>({
      query: () => ({
        url: 'Roles/Tags/Distinct',
        method: 'GET',
      }),
    }),
  }),
});
export const {
  useGetAllRolesQuery,
  useCreateRoleMutation,
  useGetRoleByIdQuery,
  useLazyGetAllRolesQuery,
  useLazyGetRoleByIdQuery,
  useUpdateRoleMutation,
  useRemoveRoleMutation,
  useRemoveRolesMutation,
  useRemoveUsersByRoleMutation,
  useUpdateUserByRoleMutation,
  useAddUserByRoleMutation,
  useGetDistinctTagsQuery,
} = rolesService;
